import React, { useState } from 'react';
import { Modal, Steps, Button, Checkbox, Upload, Table, Input, notification } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
	InboxOutlined,
	SendOutlined,
	FileTextOutlined,
	EditOutlined,
	CloseOutlined,
} from '@ant-design/icons';
import Papa from 'papaparse';
import { sanitizeString } from '@/utils/sanitizeString';
import { sendBulkSMS, updateContacts } from '@/api/textingPlatform';
import { SendBulkSMSRequest, UpdateContactsRequest } from '@/interfaces/ITextingPlatform';

const { Dragger } = Upload;

interface BulkData {
	phone_number: string;
	contact_name: string;
	message: string;
}

interface BulkMessageModalProps {
	visible: boolean;
	onClose: () => void;
	provisionedNumber: string;
	theirNumber: string;
	username: string;
	getTextingConvos: () => void;
	getContacts: () => void;
	themeColor: string;
	selectedAgentId: string;
}

const BulkMessageModal: React.FC<BulkMessageModalProps> = ({
	visible,
	onClose,
	provisionedNumber,
	theirNumber,
	username,
	selectedAgentId,
	themeColor,
}) => {
	const [currentStep, setCurrentStep] = useState<number>(0);
	const [consentChecked, setConsentChecked] = useState<boolean>(false);
	const [isCSVBulk, setIsCSVBulk] = useState<boolean>(true);
	const [file, setFile] = useState<File | null>(null);
	const [data, setData] = useState<BulkData[]>([
		{ phone_number: '+1', contact_name: '', message: '' },
	]);
	const [isSameMsgChecked, setIsSameMsgChecked] = useState<boolean>(false);

	const queryClient = useQueryClient();

	const sendBulkSMSMutation = useMutation({
		mutationFn: (request: SendBulkSMSRequest) => sendBulkSMS(request),
		onSuccess: () => {
			notification.success({ message: 'Success', description: 'Messages sent successfully' });
			queryClient.invalidateQueries({
				queryKey: ['getTextingConversations', selectedAgentId, provisionedNumber],
			});
			setCurrentStep(4);
		},
		onError: (error: Error) => {
			notification.error({
				message: 'Error',
				description: error.message || 'Failed to send one or more messages',
			});
		},
	});

	const updateContactsMutation = useMutation({
		mutationFn: (request: UpdateContactsRequest) => updateContacts(request),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['getContacts', username] });
		},
		onError: (error: Error) => {
			notification.error({
				message: 'Error',
				description: error.message || 'Failed to update contacts',
			});
		},
	});

	const resetModal = () => {
		setCurrentStep(0);
		setConsentChecked(false);
		setIsCSVBulk(true);
		setFile(null);
		setData([{ phone_number: '+1', contact_name: '', message: '' }]);
		setIsSameMsgChecked(false);
	};

	const handleClose = () => {
		resetModal();
		onClose();
	};

	const handleFileUpload = (info: any) => {
		const { file } = info;
		if (file.type !== 'text/csv') {
			notification.error({
				message: 'Error',
				description: 'Please upload a CSV file',
			});
			return false;
		}
		setFile(file.originFileObj || file);
		return false;
	};

	const handleParse = () => {
		if (!file) {
			notification.error({
				message: 'Error',
				description: 'Please select a file to continue',
			});
			return;
		}

		try {
			Papa.parse(file, {
				header: true,
				skipEmptyLines: true,
				complete(results: any) {
					const sanitizedData: BulkData[] = results.data.map((el: any) => ({
						phone_number: sanitizeString(el.phone_number || ''),
						message: sanitizeString(el.message || ''),
						contact_name: sanitizeString(el.contact_name || ''),
					}));
					setData(sanitizedData);
					setCurrentStep(2);
				},
			});
		} catch (error: any) {
			notification.error({
				message: 'Error',
				description: `There was an error parsing your file: ${error?.message || 'Unknown error'}`,
			});
		}
	};

	const checkBlastValidity = (msgs: string[]): boolean => {
		if (msgs.length === 0) {
			notification.error({
				message: 'Error',
				description: 'One or more recipients does not start with +1',
			});
			return false;
		}
		return true;
	};

	const sendBulkMessages = async () => {
		const users = data.map((el) => el.phone_number);
		const msgs = data.map((el) => el.message);

		if (!checkBlastValidity(msgs)) {
			return;
		}

		sendBulkSMSMutation.mutate({
			messages: msgs,
			from: provisionedNumber,
			theirNumber,
			recepients: users,
		});

		const contactData = data.filter((el) => el.contact_name && el.contact_name.trim().length > 0);
		if (contactData.length > 0) {
			const mapping: { [key: string]: string } = {};
			contactData.forEach((el) => {
				mapping[el.phone_number] = el.contact_name;
			});

			updateContactsMutation.mutate({ mapping, username });
		}
	};

	const addMessage = () => {
		const newMessage = isSameMsgChecked && data.length > 0 ? data[0].message : '';
		setData((prev) => [...prev, { phone_number: '+1', contact_name: '', message: newMessage }]);
	};

	const removeMessage = (indexToRemove: number) => {
		if (data.length > 1) {
			setData((prev) => prev.filter((_, index) => index !== indexToRemove));
		}
	};

	const columns = [
		{ title: 'Phone Number', dataIndex: 'phone_number', key: 'phone_number' },
		{ title: 'Message', dataIndex: 'message', key: 'message' },
		{ title: 'Contact Name', dataIndex: 'contact_name', key: 'contact_name' },
	];

	const steps = [
		{ title: 'Consent', icon: <FileTextOutlined /> },
		{
			title: isCSVBulk ? 'Upload CSV' : 'Enter Data',
			icon: isCSVBulk ? <InboxOutlined /> : <EditOutlined />,
		},
		{ title: 'Verify', icon: <FileTextOutlined /> },
		{ title: 'Send', icon: <SendOutlined /> },
	];

	return (
		<Modal
			title="Send Bulk Text Messages"
			open={visible}
			onCancel={handleClose}
			footer={null}
			width={800}
			centered
			destroyOnClose
		>
			<div className="py-4">
				{currentStep < 4 && <Steps current={currentStep} items={steps} className="mb-6" />}

				{currentStep === 0 && (
					<div className="space-y-6">
						<div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
							<Checkbox
								checked={consentChecked}
								onChange={(e) => setConsentChecked(e.target.checked)}
								className="text-sm"
							>
								By sending bulk text messages, I agree that all my contacts have opted in to
								receiving this messaging from me. If my texts are marked as spam or cold outreach, I
								understand my account will immediately be banned and I may be charged a fee for
								abuse.
							</Checkbox>
						</div>

						<div className="flex justify-between">
							<Button
								type="primary"
								onClick={() => {
									if (!consentChecked) {
										notification.error({
											message: 'Error',
											description: 'Please check the consent to continue',
										});
										return;
									}
									setCurrentStep(1);
									setIsCSVBulk(true);
								}}
								style={{ backgroundColor: themeColor, borderColor: themeColor }}
							>
								Upload CSV
							</Button>
							<Button
								type="primary"
								onClick={() => {
									if (!consentChecked) {
										notification.error({
											message: 'Error',
											description: 'Please check the consent to continue',
										});
										return;
									}
									setCurrentStep(1);
									setIsCSVBulk(false);
								}}
								style={{ backgroundColor: themeColor, borderColor: themeColor }}
							>
								Enter Manually
							</Button>
						</div>
					</div>
				)}

				{currentStep === 1 && isCSVBulk && (
					<div className="space-y-4">
						<div className="bg-blue-50 p-4 rounded-lg">
							<p className="text-sm text-gray-700 mb-2">
								Make sure your file has columns: <strong>phone_number</strong>,{' '}
								<strong>message</strong>, and optionally <strong>contact_name</strong>
							</p>
							<div className="bg-gray-100 p-3 rounded text-xs font-mono">
								phone_number,message,contact_name
								<br />
								+1xxxxxxxxxx,Hi there,John Doe
								<br />
								+1xxxxxxxxxx,You there?,Jane Smith
							</div>
						</div>

						<Dragger
							accept=".csv"
							beforeUpload={handleFileUpload}
							showUploadList={false}
							className="mb-4"
						>
							<p className="ant-upload-drag-icon">
								<InboxOutlined style={{ fontSize: '48px', color: themeColor }} />
							</p>
							<p className="ant-upload-text">{file?.name || 'Click or drag CSV file to upload'}</p>
						</Dragger>

						<div className="flex justify-between">
							<Button onClick={() => setCurrentStep(0)}>Go Back</Button>
							<Button
								type="primary"
								onClick={handleParse}
								style={{ backgroundColor: themeColor, borderColor: themeColor }}
							>
								Next
							</Button>
						</div>
					</div>
				)}

				{currentStep === 1 && !isCSVBulk && (
					<div className="space-y-4">
						<Checkbox
							checked={isSameMsgChecked}
							onChange={(e) => {
								setIsSameMsgChecked(e.target.checked);
								if (e.target.checked && data.length > 0) {
									const firstMessage = data[0].message;
									setData((prev) => prev.map((item) => ({ ...item, message: firstMessage })));
								}
							}}
						>
							Use same message for every recipient
						</Checkbox>

						<div className="space-y-2 max-h-60 overflow-y-auto">
							{data.map((item, idx) => (
								<div key={idx} className="grid grid-cols-12 gap-2 items-center">
									<div className="col-span-3">
										<Input
											placeholder="Contact name (optional)"
											value={item.contact_name}
											onChange={(e) =>
												setData((prev) => {
													const newData = [...prev];
													newData[idx].contact_name = e.target.value;
													return newData;
												})
											}
										/>
									</div>
									<div className="col-span-3">
										<Input
											placeholder="+1xxxxxxxxxx"
											value={item.phone_number}
											onChange={(e) => {
												let value = e.target.value.replace(/[^0-9+]/g, '');
												if (!value.startsWith('+1')) {
													value = '+1';
												}
												setData((prev) => {
													const newData = [...prev];
													newData[idx].phone_number = value;
													return newData;
												});
											}}
										/>
									</div>
									<div className="col-span-5">
										<Input
											placeholder="Message"
											value={item.message}
											onChange={(e) =>
												setData((prev) => {
													const newData = [...prev];
													if (isSameMsgChecked) {
														newData.forEach((item) => (item.message = e.target.value));
													} else {
														newData[idx].message = e.target.value;
													}
													return newData;
												})
											}
										/>
									</div>
									<div className="col-span-1 flex justify-center">
										{data.length > 1 && (
											<Button
												type="text"
												size="small"
												icon={<CloseOutlined />}
												onClick={() => removeMessage(idx)}
												className="text-red-500 hover:text-red-700"
											/>
										)}
									</div>
								</div>
							))}
						</div>

						<div className="flex gap-2">
							<Button
								onClick={() => setData([{ phone_number: '+1', contact_name: '', message: '' }])}
							>
								Reset
							</Button>
							<Button type="dashed" onClick={addMessage}>
								+ Add
							</Button>
						</div>

						<div className="flex justify-between">
							<Button onClick={() => setCurrentStep(0)}>Go Back</Button>
							<Button
								type="primary"
								onClick={() => {
									const msgs = data.map((el) => el.message);
									if (checkBlastValidity(msgs)) {
										setCurrentStep(2);
									}
								}}
								style={{ backgroundColor: themeColor, borderColor: themeColor }}
							>
								Next
							</Button>
						</div>
					</div>
				)}

				{currentStep === 2 && (
					<div className="space-y-4">
						<Table
							columns={columns}
							dataSource={data}
							pagination={false}
							scroll={{ y: 300 }}
							size="small"
						/>

						<div className="flex justify-between">
							<Button onClick={() => setCurrentStep(1)}>Back</Button>
							<Button
								type="primary"
								onClick={() => setCurrentStep(3)}
								style={{ backgroundColor: themeColor, borderColor: themeColor }}
							>
								Confirm
							</Button>
						</div>
					</div>
				)}

				{currentStep === 3 && (
					<div className="text-center space-y-6">
						<div>
							<h3 className="text-2xl font-semibold mb-2">
								Send <span style={{ color: themeColor }}>{data.length}</span> messages?
							</h3>
							<p className="text-gray-600">This action cannot be undone</p>
						</div>

						<Button
							type="primary"
							size="large"
							icon={<SendOutlined />}
							onClick={sendBulkMessages}
							loading={sendBulkSMSMutation.isPending}
							style={{ backgroundColor: themeColor, borderColor: themeColor }}
							className="text-xl px-8 py-4 h-auto"
						>
							🚀 Send Messages
						</Button>
					</div>
				)}

				{currentStep === 4 && (
					<div className="text-center space-y-6">
						<div>
							<h3 className="text-2xl font-semibold mb-2">
								🎉 <span style={{ color: themeColor }}>{data.length}</span> Messages Sent!
							</h3>
							<p className="text-xl text-gray-600">Woohoo! You've just sent a text blast</p>
						</div>

						<Button type="primary" onClick={handleClose} size="large">
							Close
						</Button>
					</div>
				)}
			</div>
		</Modal>
	);
};

export default BulkMessageModal;
